import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AdminPayments() {
  const [filters, setFilters] = useState({ status: "", tier_id: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });

  useEffect(() => { (async () => {
    const params = new URLSearchParams({ page: String(page), page_size: "25" });
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    try { setData((await api.get(`/admin/payments?${params.toString()}`)).data); } catch {}
  })(); }, [filters, page]);

  const copy = (text) => { navigator.clipboard.writeText(text).then(() => toast.success("Copied")); };
  const pageRevenue = data.items.filter(p => p.payment_status === "paid").reduce((sum, p) => sum + Number(p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <header><h1 className="font-serif text-3xl text-ink tracking-tight">Payments</h1><p className="text-ink-soft text-sm mt-1">{data.total} total · Page revenue ${pageRevenue.toFixed(2)}</p></header>

      <div className="flex flex-wrap gap-3">
        <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
          className="bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="payments-status">
          <option value="">All statuses</option>
          <option value="paid">paid</option>
          <option value="unpaid">unpaid</option>
        </select>
        <select value={filters.tier_id} onChange={(e) => { setFilters({ ...filters, tier_id: e.target.value }); setPage(1); }}
          className="bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="payments-tier">
          <option value="">All tiers</option>
          <option value="transfer_audit">transfer_audit</option>
          <option value="platform">platform</option>
          <option value="florida_transfer">florida_transfer</option>
          <option value="selective_elite">selective_elite</option>
        </select>
      </div>

      <div className="border border-stone/40 bg-bone overflow-x-auto" data-testid="payments-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-bone-dim border-b border-stone/40 text-[10px] uppercase tracking-[0.18em] text-stone">
          <div className="col-span-2">Date</div>
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Tier</div>
          <div className="col-span-1">Amount</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">Session ID</div>
          <div className="col-span-1">Referral</div>
        </div>
        {data.items.length === 0 ? <div className="px-4 py-6 text-sm text-ink-soft">No payments.</div> : data.items.map((p) => (
          <div key={p.session_id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-stone/20 items-baseline" data-testid={`payment-row-${p.session_id}`}>
            <div className="col-span-2 text-[10px] text-stone font-mono tabular-nums">{(p.created_at || "").slice(0, 10)}</div>
            <div className="col-span-3 text-xs text-ink-soft truncate font-mono">{(p.metadata?.user_email || p.metadata?.buyer_email || "—")}</div>
            <div className="col-span-2 text-xs text-ink truncate">{p.tier_id}</div>
            <div className="col-span-1 text-sm text-ink tabular-nums">${Number(p.amount || 0).toFixed(0)}</div>
            <div className="col-span-1 text-[10px] uppercase tracking-[0.18em]">
              <span className={p.payment_status === "paid" ? "text-ivy" : "text-brass"}>{p.payment_status}</span>
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <code className="text-[10px] font-mono text-ink-soft truncate">{(p.session_id || "").slice(0, 14)}…</code>
              <button onClick={() => copy(p.session_id)} className="text-[9px] uppercase tracking-[0.22em] text-ivy hover:text-brass">Copy</button>
            </div>
            <div className="col-span-1 text-[10px]">
              {p.metadata?.referral_code_used && <span className="text-ivy">{p.metadata.referral_code_used}</span>}
              {Number(p.metadata?.credits_applied_cents || 0) > 0 && <span className="text-brass">−${(Number(p.metadata.credits_applied_cents) / 100).toFixed(0)} credit</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between text-xs text-stone">
        <span>Page {data.page} of {Math.max(1, Math.ceil(data.total / data.page_size))}</span>
        <div className="flex gap-3">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border border-stone/40 px-3 py-1 disabled:opacity-40">Prev</button>
          <button disabled={page * data.page_size >= data.total} onClick={() => setPage(page + 1)} className="border border-stone/40 px-3 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}
